import { FASSUNG } from "../fassung";
import { istRuhig, regungSeit, schreibtNoch, stilleSeit } from "../schale/ruhe";

/* ══════════════════════════════════════════════════════════════════
   Den Service Worker anmelden — und eine neue Fassung von selbst übernehmen.

   09-mobile.md, *„Warum PWA und nicht App Store"*: *„Ein Update ist ein
   Deployment."* Luca, 19.09.2026: *„Ich möchte, dass es automatisch
   aktualisiert wird."*

   ── Was bis zum 19.09.2026 galt, und warum es nicht reichte ────────

   Der Service Worker hieß `/sw.js?v=<commit>`, und eine neue Fassung kam
   als Band mit dem Knopf „Laden". Zwei Fehler steckten darin:

   1. **Eine offene Seite merkte eine neue Fassung nie.** Geprüft wurde
      beim Zurückkommen in den Vordergrund — aber die Prüfung holt
      **dieselbe** Adresse, `/sw.js?v=<alter commit>`, und die Datei war
      immer dieselbe. Neu kam nur, wer die Seite ohnehin frisch lud.
   2. **Der Knopf verlangte eine Entscheidung, die niemand treffen kann.**
      Wer auf dem Hof steht, weiß nicht, ob „Laden" jetzt passt. Also
      tippte er „Später", und das Band kam am nächsten Tag wieder.

   ── Was jetzt gilt ────────────────────────────────────────────────

   **Bemerken.** Der Service Worker heißt immer `/sw.js`, und jeder Bau
   schreibt seine Kennung hinein (`public/sw.js`, `vite.config.ts`). Geprüft
   wird beim Laden, beim Wechsel in den Hinter- und Vordergrund, wenn das
   Netz wiederkommt und alle 15 Minuten, solange die Seite sichtbar ist. Der
   Browser baut eine neue Fassung dann im Hintergrund ein — samt allen
   Stücken, damit sie auch im Funkloch vollständig ist.

   **Unterscheiden.** Die Seite fragt den wartenden Service Worker nach
   seiner Kennung. Ist es ihre eigene, hinkte nur er nach (die Seite kam
   schon frisch aus dem Netz) — dann übernimmt er still, ohne Neuladen. Ist
   es eine andere, ist die Seite alt.

   **Übernehmen — nur im sicheren Moment** (`schale/ruhe.ts`):

   | Moment | Bedingung |
   |---|---|
   | **Wegwechsel** — jemand geht auf eine andere Maske | nichts unterwegs zum Server oder ins Gerät. Aus dem Wechsel im Router wird ein volles Laden desselben Ziels (`router.tsx`) |
   | **Gerade erschienen** — frisch geöffnet oder zurück in den Vordergrund, und seitdem nichts angefasst (10 s) | nichts in Arbeit |
   | **Hintergrund** — App gewechselt, Bildschirm aus | nichts in Arbeit |
   | **Lange Ruhe** — 15 Minuten sichtbar und unberührt | nichts in Arbeit |

   „Nichts in Arbeit" heißt: kein Blatt offen, kein Finger in einem Feld,
   an dieser Maske seit dem letzten Wegwechsel nichts geklickt oder
   getippt, nichts unterwegs. **Mitten in einer Eingabe wird nie neu
   geladen** — dann nimmt der nächste Wegwechsel die neue Fassung mit.

   **Kein Knopf, kein Band.** Nach dem Wechsel steht einmal kurz, dass es
   die neue Fassung ist (`bausteine/Offline.tsx`, `Fassungshinweis`) — damit
   das kurze Neuladen einen Grund hat, den man lesen kann.

   **Kein Endlos-Neuladen.** Für dieselbe Fassung wird höchstens einmal in
   zehn Minuten neu geladen (`SCHUTZ`). Käme nach dem Neuladen trotzdem die
   alte Seite — ein Zwischenspeicher irgendwo dazwischen —, bleibt sie
   stehen, statt im Kreis zu laden.

   **In der Entwicklung gar nicht.** Der Vite-Entwicklungsserver liefert
   Module einzeln aus; ein Service Worker, der sie ablegt, zeigt beim
   nächsten Speichern den Stand von vorhin. Zum Ausprobieren
   `npx vite build && npx vite preview` — dort ist es der gebaute Stand.
   ══════════════════════════════════════════════════════════════════ */

const WEG = "/sw.js";
/** Die Kennung dieses Baus — dieselbe steht in `sw.js`. */
const EIGENE = FASSUNG.bau;

/** Wie lange eine Seite nach dem Erscheinen noch ungefragt neu laden darf. */
const FRISCH_MS = 10_000;
/** So lange ohne Regung, dann ist lange Ruhe. */
const RUHE_MS = 15 * 60_000;
/** So oft wird nachgesehen, solange die Seite sichtbar ist. */
const PRUEFEN_MS = 15 * 60_000;
/** Seltener nicht — mehrere Anlässe kurz hintereinander sind eine Prüfung. */
const PRUEFEN_HOECHSTENS_MS = 30_000;
/** Für dieselbe Fassung höchstens einmal in dieser Zeit neu laden. */
const SCHUTZ_MS = 10 * 60_000;

const SCHUTZ = "machine-office:fassung-geladen";
const HINWEIS = "machine-office:fassung-gewechselt";
const STUECK = "machine-office:stueck-fehlte";

/** Eine neue Fassung, die bereitsteht — und der Service Worker, der sie bringt. */
type Bereit = { sw: ServiceWorker | null; fassung: string };

let bereit: Bereit | null = null;
let laedt = false;
let anmeldung: ServiceWorkerRegistration | null = null;
let zuletztGeprueft = 0;
/** Wann die Seite zuletzt erschienen ist — geladen oder in den Vordergrund geholt. */
let erschienen = Date.now();

/* ── Kleine Helfer für den Sitzungsspeicher ── */

function lesen(schluessel: string): string | null {
  try { return sessionStorage.getItem(schluessel); } catch { return null; }
}
function schreiben(schluessel: string, wert: string | null): void {
  try {
    if (wert === null) sessionStorage.removeItem(schluessel);
    else sessionStorage.setItem(schluessel, wert);
  } catch { /* Ohne Speicher: kein Schutz, kein Hinweis — die Seite läuft trotzdem. */ }
}

/**
 * Darf für diese Fassung jetzt neu geladen werden?
 *
 * Merkt sich die Fassung und die Uhrzeit. Wer es für dieselbe Fassung binnen
 * zehn Minuten noch einmal versucht, bekommt `false` — dann ist beim letzten
 * Mal etwas schiefgegangen, und ein weiteres Neuladen machte es nicht besser.
 */
function schutzErlaubt(fassung: string): boolean {
  const roh = lesen(SCHUTZ);
  if (roh) {
    try {
      const alt = JSON.parse(roh) as { fassung?: string; am?: number };
      if (alt.fassung === fassung && typeof alt.am === "number" && Date.now() - alt.am < SCHUTZ_MS) {
        console.warn(`Neue Fassung ${fassung}: schon einmal neu geladen, ohne dass sie kam — bleibt stehen.`);
        return false;
      }
    } catch { /* kaputter Eintrag — wird überschrieben */ }
  }
  schreiben(SCHUTZ, JSON.stringify({ fassung, am: Date.now() }));
  return true;
}

/** Zwei Adressen, einmal kodiert und einmal nicht — dieselbe? */
function gleicheAdresse(a: string, b: string): boolean {
  const klar = (x: string) => { try { return decodeURI(x); } catch { return x; } };
  return klar(a) === klar(b);
}

/** „Welche Fassung bist du?" — über einen eigenen Kanal, höchstens drei Sekunden. */
function fassungFragen(sw: ServiceWorker): Promise<string | null> {
  return new Promise((antwort) => {
    const kanal = new MessageChannel();
    const uhr = window.setTimeout(() => antwort(null), 3000);
    kanal.port1.onmessage = (e: MessageEvent<{ fassung?: unknown }>) => {
      window.clearTimeout(uhr);
      antwort(typeof e.data?.fassung === "string" ? e.data.fassung : null);
    };
    try {
      sw.postMessage({ typ: "fassung" }, [kanal.port2]);
    } catch {
      window.clearTimeout(uhr);
      antwort(null);
    }
  });
}

/**
 * Übernehmen und neu laden — jetzt.
 *
 * Gibt zurück, ob es losgeht. Erst wird der wartende Service Worker
 * vorgelassen, **dann** geladen: Sonst lüde die Seite neu, während noch der
 * alte bedient, und die neue Fassung käme im Funkloch nicht aus dem Lager.
 * Kommt der Wechsel nicht binnen vier Sekunden, wird trotzdem geladen — die
 * Navigation geht zuerst ins Netz und bringt die neue Seite auch so.
 *
 * `ziel` ist die Adresse, auf die der Router gerade wechselt. Er hat sie
 * schon in den Verlauf geschrieben, bevor er fragt; also wird **diese**
 * Seite neu geladen und kein neuer Eintrag angelegt — die Zurückgeste führt
 * danach dorthin, wohin sie ohne neue Fassung auch geführt hätte.
 */
function uebernehmen(ziel: string | null): boolean {
  if (laedt || !bereit) return false;
  if (!schutzErlaubt(bereit.fassung)) {
    bereit = null;
    return false;
  }
  laedt = true;
  schreiben(HINWEIS, bereit.fassung);
  const sw = bereit.sw;
  void (async () => {
    if (sw && sw.state === "installed") {
      const gewechselt = new Promise<void>((fertig) => {
        navigator.serviceWorker.addEventListener("controllerchange", () => fertig(), { once: true });
        window.setTimeout(fertig, 4000);
      });
      sw.postMessage({ typ: "uebernehmen" });
      await gewechselt;
    }
    const hier = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (ziel === null || gleicheAdresse(hier, ziel)) window.location.reload();
    else window.location.assign(ziel);
  })();
  return true;
}

/** Die Momente ohne Wegwechsel (Tabelle oben). */
function momentPruefen(): void {
  if (!bereit || laedt) return;
  const verdeckt = document.visibilityState === "hidden";
  const frisch = Date.now() - erschienen <= FRISCH_MS && !regungSeit(erschienen);
  const ruhe = !verdeckt && stilleSeit() >= RUHE_MS && Date.now() - erschienen >= RUHE_MS;
  if ((verdeckt || frisch || ruhe) && istRuhig()) uebernehmen(null);
}

/**
 * Der Router fragt beim Wechsel auf eine andere Maske (`router.tsx`).
 *
 * Steht eine neue Fassung bereit und ist nichts unterwegs, wird aus dem
 * Wechsel ein volles Laden — und das Versprechen erfüllt sich nie, damit der
 * Router die alte Maske stehen lässt, bis die Seite geht. Nach acht Sekunden
 * erfüllt es sich doch: Ist die Seite dann noch da, ist beim Laden etwas
 * schiefgegangen, und der Wechsel läuft wie immer weiter.
 */
export function fassungBeimWegwechsel(ziel: string): Promise<void> {
  if (!bereit || laedt || schreibtNoch()) return Promise.resolve();
  if (!uebernehmen(ziel)) return Promise.resolve();
  return new Promise((weiter) => { window.setTimeout(weiter, 8000); });
}

/** Eine neue Fassung ist da — oder die Seite hat gemerkt, dass sie alt ist. */
function bereitMelden(sw: ServiceWorker | null, fassung: string | null): void {
  bereit = { sw, fassung: fassung ?? "unbekannt" };
  momentPruefen();
}

/**
 * Ein Service Worker wartet. Wessen Fassung ist er?
 *
 * Die eigene: Die Seite kam frisch aus dem Netz, nur er hinkte nach — er
 * übernimmt still, und nichts wird neu geladen. Eine andere: Die Seite ist
 * alt, die neue steht bereit.
 */
async function wartendenPruefen(reg: ServiceWorkerRegistration): Promise<void> {
  const sw = reg.waiting;
  if (!sw || bereit?.sw === sw) return;
  const fassung = await fassungFragen(sw);
  if (fassung === EIGENE) {
    sw.postMessage({ typ: "uebernehmen" });
    return;
  }
  bereitMelden(sw, fassung);
}

/** Nachsehen, ob es eine neue Fassung gibt — gedrosselt. */
function nachsehen(): void {
  if (!anmeldung || laedt || !navigator.onLine) return;
  if (Date.now() - zuletztGeprueft < PRUEFEN_HOECHSTENS_MS) return;
  zuletztGeprueft = Date.now();
  anmeldung.update().catch(() => { /* ohne Netz — beim nächsten Anlass */ });
}

/**
 * Dem Service Worker sagen, was die Seite schon geladen hat.
 *
 * Mit der Liste aller Stücke (`RUMPF` in `sw.js`) liegt ohnehin alles im
 * Lager. Das hier bleibt der Rückfall für einen Bau ohne Liste — und kostet
 * nichts, weil der Service Worker überspringt, was schon liegt.
 */
function rumpfNachlegen(): void {
  const bedient = navigator.serviceWorker.controller;
  if (!bedient) return;
  const wege = performance
    .getEntriesByType("resource")
    .map((eintrag) => eintrag.name)
    .filter((name) => name.startsWith(window.location.origin) && name.includes("/assets/"));
  if (wege.length) bedient.postMessage({ typ: "nachlegen", wege });
}

/**
 * War die Seite eben die neue Fassung geworden? Dann einmal sagen.
 *
 * Liest den Merker, den `uebernehmen` vor dem Neuladen setzt, und löscht ihn.
 * Gibt `true` nur, wenn die Seite jetzt wirklich die Fassung ist, für die neu
 * geladen wurde.
 */
export function fassungGewechselt(): boolean {
  const fuer = lesen(HINWEIS);
  if (fuer === null) return false;
  schreiben(HINWEIS, null);
  if (fuer !== EIGENE) return false;
  schreiben(SCHUTZ, null);
  return true;
}

/**
 * Ein Stück der Seite fehlt auf dem Server.
 *
 * Das passiert einer Seite, die nach dem Ausliefern noch die alte ist: Die
 * Stücke tragen ihre Prüfsumme im Namen, und nach dem Umschalten liefert der
 * Server nur noch die neuen (`betrieb/ausliefern.sh`). Vite meldet das als
 * `vite:preloadError`. Die Antwort ist dieselbe wie beim Wegwechsel: Die
 * Seite lädt neu, das Ziel steht schon in der Adresse — höchstens einmal in
 * dreißig Sekunden, damit ein wirklich fehlendes Stück nicht im Kreis lädt.
 */
function stueckFehltHorchen(): void {
  window.addEventListener("vite:preloadError", (e) => {
    const zuletzt = Number(lesen(STUECK) ?? 0);
    if (Date.now() - zuletzt < 30_000) return;
    e.preventDefault();
    schreiben(STUECK, String(Date.now()));
    const bisRuhe = Date.now() + 3000;
    const warten = () => {
      if (schreibtNoch() && Date.now() < bisRuhe) window.setTimeout(warten, 200);
      else window.location.reload();
    };
    warten();
  });
}

export function serviceWorkerAnmelden(): void {
  if (!("serviceWorker" in navigator)) return;
  // `import.meta.env.DEV` ist beim Bau eine Konstante; der Zweig fällt heraus.
  if (import.meta.env.DEV) return;

  stueckFehltHorchen();

  /* Angemeldet wird nach `load`: Der Einbau holt den Vorrat, und das mit dem
     ersten Bild um die Leitung zu bringen, macht genau den Start langsam,
     für den die PWA gebaut ist. */
  window.addEventListener("load", () => {
    /* `updateViaCache: "none"`: Die Prüfung holt `sw.js` immer am
       Zwischenspeicher vorbei. Browser tun das für die Datei selbst ohnehin;
       gesagt ist gesagt. */
    void navigator.serviceWorker.register(WEG, { updateViaCache: "none" }).then((reg) => {
      anmeldung = reg;
      zuletztGeprueft = Date.now(); // `register` prüft beim Anmelden selbst
      void wartendenPruefen(reg);
      void navigator.serviceWorker.ready.then(rumpfNachlegen);

      reg.addEventListener("updatefound", () => {
        const neu = reg.installing;
        if (!neu) return;
        neu.addEventListener("statechange", () => {
          if (neu.state === "installed") void wartendenPruefen(reg);
        });
      });

      /* **Der Bediener wechselt.** Entweder hat diese Seite ihn gebeten
         (dann lädt `uebernehmen` gleich neu) — oder ein anderer Reiter hat
         eine neue Fassung übernommen, und diese Seite ist jetzt alt, während
         schon der neue Service Worker bedient. Dann steht die neue Fassung
         für sie bereit wie jede andere. */
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        rumpfNachlegen();
        if (laedt) return;
        const bedient = navigator.serviceWorker.controller;
        if (!bedient) return;
        void fassungFragen(bedient).then((fassung) => {
          if (fassung !== null && fassung !== EIGENE) bereitMelden(null, fassung);
        });
      });

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") erschienen = Date.now();
        // Beim Gehen nachsehen, damit sie beim Wiederkommen schon da ist; beim
        // Kommen, falls das Telefon lange in der Tasche lag.
        nachsehen();
        momentPruefen();
      });
      window.addEventListener("online", nachsehen);
      window.setInterval(() => {
        if (document.visibilityState !== "visible") return;
        if (Date.now() - zuletztGeprueft >= PRUEFEN_MS) nachsehen();
        momentPruefen();
      }, 60_000);
    }, () => {
      // Ein Browser ohne Service Worker (privates Fenster, alte Fassung) ist
      // kein Fehler, den jemand sehen müsste: Die Anwendung läuft, nur ohne
      // Offline. Der Schirm sagt das an der Stelle, an der es zählt.
    });
  });
}
